import { Button } from "@gigpro/ui/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuPortal, DropdownMenuTrigger } from "@gigpro/ui/DropdownMenu";
import { ChevronDown, Ellipsis } from "@gigpro/ui/Icons";
import { HeaderCrawler } from "components/Halloween/HeaderCrawler";
import { HeaderFlyover } from "components/Halloween/HeaderFlyover";
import { HeaderSkyline } from "components/Halloween/HeaderSkyline";
import { LogoDecor } from "components/Halloween/LogoDecor";
import type { NavItem, NavItemLink } from "layouts/BaseLayout/types";
import { useFeatureFlag } from "providers/LaunchDarkly";
import { DesktopNavItem } from "./DesktopNavItem";
import { DropdownNavItem } from "./DropdownNavItem";
import { SuperAdminButton } from "./SuperAdminButton";

type MainNavigationProps = {
  logo: React.ReactNode;
  modeSwitcher?: React.ReactNode;
  mainItems: Array<NavItem>;
  overflowItems?: Array<NavItem>;
  adminItems?: Array<NavItem>;
  inbox: NavItemLink;
  agentChat: NavItem | null;
  userProfile: React.ReactNode;
};

export function MainNavigation({
  logo,
  modeSwitcher,
  mainItems,
  adminItems,
  overflowItems,
  inbox,
  agentChat,
  userProfile,
}: Readonly<MainNavigationProps>) {
  const halloween = useFeatureFlag("halloween-easter-eggs");

  return (
    <div
      style={{ viewTransitionName: "main-navigation" }}
      className="fixed inset-x-0 top-0 z-50 flex h-nav-top items-center gap-2 overflow-hidden bg-black px-3 text-large font-semibold text-white xl:gap-3 xl:px-5"
    >
      {/* The bar is a stacking context, so -z-10 paints above its background and below every link. */}
      {halloween && <HeaderSkyline className="-z-10 h-4" />}
      {halloween && <HeaderFlyover />}
      {halloween && <HeaderCrawler />}
      {halloween ? (
        <div className="relative flex shrink-0">
          {logo}
          <LogoDecor />
        </div>
      ) : (
        logo
      )}
      {modeSwitcher}
      <div className="flex h-full min-w-0 grow-100 items-center gap-1 overflow-auto xl:gap-3">
        {mainItems.filter(isLinkOrButton).map((item) => (
          <DesktopNavItem key={item.title} item={item} className="h-full" />
        ))}
      </div>
      <div className="-mr-2 grow-3" />
      <DesktopNavItem item={inbox} hideText className="h-full" />
      {agentChat && isLinkOrButton(agentChat) && <DesktopNavItem item={agentChat} hideText className="h-full" />}
      {!!overflowItems?.length && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="nav-dropdown" className="-ml-1.5 rounded-full p-1.5 text-white hover:bg-white/20">
              <Ellipsis className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className="w-48 p-0">
            {overflowItems.filter(isLinkOrButton).map((item) => (
              <DropdownNavItem key={item.title} item={item} />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {!!adminItems?.length && (
        <div className="relative flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger className="group relative z-10 flex items-center justify-between gap-1 rounded-sm bg-white py-1 pr-1.5 pl-2.5 text-large/5 tracking-tight text-brand">
              Admin
              <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent side="bottom" className="w-48 p-0">
                {adminItems.filter(isLinkOrButton).map((item) => (
                  <DropdownNavItem key={item.title} item={item} />
                ))}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
          <SuperAdminButton />
        </div>
      )}
      {userProfile}
    </div>
  );
}

function isLinkOrButton(item: NavItem) {
  return item.type === "link" || item.type === "button";
}
